import React, { useEffect, useState } from "react";
import { Calendar, Clock, MapPin, Ticket, ArrowRight, Mail, Facebook, Instagram, Star } from "lucide-react";
import api from "../lib/api";
import AppDownloadSection from "../components/AppDownloadSection";
import { useSiteSettings, isWidgetVisible } from "../lib/siteSettings";

const ORG_LOGOS = [
  { src: "/images/ec1_logo.webp", name: "EC1 Łódź" },
  { src: "/images/planszowkowi_astronauci_logo.webp", name: "Planszówkowi Astronauci" },
  { src: "/images/ksp_logo.webp", name: "KSP" },
  { src: "/images/cknii_logo.png", name: "Centrum Komiksu i Narracji Interaktywnej" },
];

function useCountdown(target) {
  const [t, setT] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, started: false });
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      // July 18 & 19 of current year
      const year = now.getFullYear();
      const day1 = new Date(year, 6, 18, 10, 0, 0);
      const day2End = new Date(year, 6, 19, 23, 59, 59);
      if (now >= day1 && now <= day2End) {
        setT({ days: 0, hours: 0, minutes: 0, seconds: 0, started: true });
        return;
      }
      let next = day1;
      if (now > day2End) next = new Date(year + 1, 6, 18, 10, 0, 0);
      const diff = next.getTime() - now.getTime();
      setT({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
        started: false,
      });
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [target]);
  return t;
}

const pad = (n) => String(n).padStart(2, "0");

export default function HomePage() {
  const [ticketUrl, setTicketUrl] = useState("https://lodzyouplay.pl/bilety");
  const c = useCountdown();
  const siteSettings = useSiteSettings();

  const showCountdown = isWidgetVisible(siteSettings, "countdown");
  const showBuyTicket = isWidgetVisible(siteSettings, "buyTicket");
  const showAppDownload = isWidgetVisible(siteSettings, "appDownload");
  const seeYouNextYear = siteSettings?.messages?.seeYouNextYear || "Zapraszamy za rok!";

  useEffect(() => {
    api.get("/settings").then((r) => {
      if (r.data?.ticketUrl) setTicketUrl(r.data.ticketUrl);
    }).catch(() => {});
  }, []);

  return (
    <div style={{ backgroundColor: "#F0F6FF" }} className="min-h-full">
      {/* HERO */}
      <section
        className="relative flex items-center justify-center overflow-hidden h-[520px] sm:h-[640px] md:h-[720px]"
        data-testid="hero-section"
      >
        <img
          src="/images/lyp_tlo.webp"
          alt=""
          width="800"
          height="1131"
          className="absolute inset-0 w-full h-full object-cover"
         />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0,20,60,0.6)" }} />
        <div className="relative z-10 flex flex-col items-center gap-[10px] px-4 w-full">
          <img
            src="/images/lyp_logo_new.webp"
            alt="Łódź You Play"
            width="596"
            height="596"
            fetchPriority="high"
            className="object-contain h-[260px] sm:h-[360px] md:h-[440px]"
            style={{ width: 900, maxWidth: "92vw" }}
            data-testid="hero-logo"
           />

          {!showCountdown ? (
            <div
              className="flex items-center gap-3 px-8 py-[16px] rounded-[18px]"
              style={{
                backgroundColor: "rgba(0,0,0,0.5)",
                border: "2px solid rgba(255,153,0,0.5)",
              }}
              data-testid="see-you-next-year"
            >
              <Star size={22} color="#FF9900" fill="#FF9900" />
              <span className="text-xl sm:text-3xl" style={{ color: "#FF9900", fontWeight: 900, letterSpacing: 1 }}>
                {seeYouNextYear}
              </span>
              <Star size={22} color="#FF9900" fill="#FF9900" />
            </div>
          ) : c.started ? (
            <div
              className="flex items-center gap-4 px-10 py-[18px] rounded-[18px]"
              style={{
                backgroundColor: "rgba(0,0,0,0.5)",
                border: "2px solid rgba(255,153,0,0.5)",
              }}
              data-testid="festival-open-banner"
            >
              <Star size={28} color="#FF9900" fill="#FF9900" />
              <span style={{ color: "#FF9900", fontSize: 32, fontWeight: 900, letterSpacing: 1 }}>
                Festiwal Otwarty!
              </span>
              <Star size={28} color="#FF9900" fill="#FF9900" />
            </div>
          ) : (
            <div className="flex items-center gap-1 sm:gap-2" data-testid="countdown">
              {[
                { v: c.days, label: "dni" },
                { v: pad(c.hours), label: "godzin" },
                { v: pad(c.minutes), label: "minut" },
                { v: pad(c.seconds), label: "sekund" },
              ].map((it, idx) => (
                <React.Fragment key={it.label}>
                  <div
                    className="flex flex-col items-center px-2 sm:px-[18px] py-2 sm:py-3 rounded-[14px] min-w-[58px] sm:min-w-[80px]"
                    style={{
                      backgroundColor: "rgba(0,0,0,0.4)",
                      border: "1px solid rgba(255,153,0,0.3)",
                    }}
                  >
                    <span className="text-2xl sm:text-4xl" style={{ color: "#FF9900", fontWeight: 900, letterSpacing: 1, lineHeight: 1 }}>
                      {it.v}
                    </span>
                    <span
                      className="text-[9px] sm:text-[11px]"
                      style={{
                        color: "rgba(255,255,255,0.7)",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        marginTop: 4,
                      }}
                    >
                      {it.label}
                    </span>
                  </div>
                  {idx < 3 && (
                    <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 32, fontWeight: 300 }}>:</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {showCountdown && (
            <div
              className="flex flex-wrap items-center justify-center px-4 sm:px-7 py-3 rounded-[30px] gap-2 sm:gap-4"
              style={{ backgroundColor: "rgba(0,0,0,0.35)" }}
              data-testid="festival-date-line"
            >
              <div className="flex items-center gap-2">
                <Calendar size={16} color="#FF9900" />
                <span className="text-sm sm:text-base" style={{ color: "#fff", fontWeight: 600 }}>18-19 Lipca 2026</span>
              </div>
              <div className="w-px h-5 hidden sm:block" style={{ backgroundColor: "rgba(255,255,255,0.3)" }} />
              <div className="flex items-center gap-2">
                <MapPin size={16} color="#00B0B0" />
                <span className="text-sm sm:text-base" style={{ color: "#fff", fontWeight: 600 }}>EC1 Łódź</span>
              </div>
            </div>
          )}

          {/* Godziny otwarcia festiwalu — pokazywane tylko gdy countdown aktywny */}
          {showCountdown && (
            <div
              className="flex flex-wrap items-center justify-center px-4 sm:px-7 py-2 rounded-[24px] gap-3 sm:gap-5 mt-1"
              style={{ backgroundColor: "rgba(0,0,0,0.28)" }}
              data-testid="festival-hours"
            >
            <div className="flex items-center gap-2">
              <Clock size={15} color="#FFD666" />
              <span className="text-xs sm:text-sm" style={{ color: "#fff", fontWeight: 600 }}>
                Sob&nbsp;10:00–24:00
              </span>
            </div>
            <div className="w-px h-4 hidden sm:block" style={{ backgroundColor: "rgba(255,255,255,0.3)" }} />
            <div className="flex items-center gap-2">
              <Clock size={15} color="#FFD666" />
              <span className="text-xs sm:text-sm" style={{ color: "#fff", fontWeight: 600 }}>
                Nd&nbsp;10:00–18:00
              </span>
            </div>
          </div>
          )}

          {showBuyTicket && (
            <a
              href={ticketUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 sm:gap-3 mt-1 transition-transform hover:scale-105 px-6 py-3 sm:px-9 sm:py-4 rounded-[14px]"
              style={{
                backgroundColor: "#FF9900",
                boxShadow: "0 4px 12px rgba(212,85,0,0.4)",
              }}
              data-testid="hero-cta-tickets"
            >
              <Ticket size={20} color="#fff" />
              <span className="text-base sm:text-xl" style={{ color: "#fff", fontWeight: 800, letterSpacing: 0.5 }}>
                Kup bilet
              </span>
              <ArrowRight size={18} color="#fff" />
            </a>
          )}
        </div>
      </section>

      {/* Organizatorzy */}
      <section className="px-4 sm:px-6 md:px-10 py-3" data-testid="organizers-section">
        <div
          className="flex flex-col sm:flex-row items-center px-4 sm:px-8 py-4 sm:py-5 rounded-[14px] gap-4 sm:gap-6"
          style={{ backgroundColor: "#fff", border: "1px solid #E0ECF5" }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, color: "#5A7A9A", letterSpacing: 0.5 }}>
            Organizatorzy:
          </span>
          <div className="flex-1 flex items-center justify-center gap-4 sm:gap-8 flex-wrap">
            {ORG_LOGOS.map((o) => (
              <div key={o.name} className="flex items-center gap-[10px] w-full sm:w-auto justify-center sm:justify-start max-w-[280px]">
                <img src={o.src} alt={o.name} width="48" height="48" style={{ width: 48, height: 48, objectFit: "contain", flexShrink: 0 }}  loading="lazy"/>
                <span
                  className="text-sm sm:text-[15px]"
                  style={{ fontWeight: 600, color: "#002D6A", lineHeight: 1.25, wordBreak: "break-word", overflowWrap: "anywhere", flex: 1 }}
                >
                  {o.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Aplikacja mobilna */}
      {showAppDownload && <AppDownloadSection />}

      {/* Kontakt */}
      <section className="px-4 sm:px-6 md:px-10 pt-1 pb-3" data-testid="contact-section">
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-2 px-4 sm:px-6 py-[14px] rounded-[14px]"
          style={{ backgroundColor: "#fff", border: "1px solid #E0ECF5" }}
        >
          {[
            { Icon: Mail, label: "Email", value: "lodzyouplay@gmail.com", url: "mailto:lodzyouplay@gmail.com", color: "#0049A8" },
            { Icon: Facebook, label: "Facebook", value: "/lodzyouplay", url: "https://facebook.com/lodzyouplay", color: "#1877F2" },
            { Icon: Instagram, label: "Instagram", value: "@lodzyouplay", url: "https://instagram.com/lodzyouplay", color: "#E4405F" },
          ].map((c) => (
            <a
              key={c.label}
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-[10px] px-4 py-[6px] rounded-[10px] transition-colors hover:bg-blue-50"
            >
              <div
                className="flex items-center justify-center"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  backgroundColor: c.color + "15",
                }}
              >
                <c.Icon size={22} color={c.color} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#5A7A9A" }}>{c.label}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#002D6A" }}>{c.value}</div>
              </div>
            </a>
          ))}
        </div>
      </section>

      <div style={{ height: 24 }} />
    </div>
  );
}
